/**
 * Help panel content split into sections for collapsible rendering.
 */

/** Intro paragraph shown above the capabilities list (always visible). */
export const HELP_INTRO = `**TACO (Technical Architecture Composer)** is an AI-powered assistant that uses natural language interaction to generate comprehensive AWS architecture solution packages. It guides AWS field teams toward complete customer solutions, helps accelerate deal progression, and equips teams to lead the right technical and business conversations.`;

/** Use Cases section (collapsible) — rendered as markdown table. */
export const HELP_ARTIFACTS = `TACO can help you deliver the following artifacts across the entire engagement lifecycle.

| **Artifact type** | **Description** |
|---|---|
| Architecture specifications | Complete AWS architecture documents with service interactions, data paths, and deployment boundaries |
| Architecture diagrams | Draw.io, PNG, SVG — solution, network, landing zone, deployment, and integration views |
| Cost analysis reports | Detailed pricing breakdowns with optimization recommendations |
| TCO analysis | Total Cost of Ownership with multi-year projections and comparisons |
| Interactive cost calculators | Standalone, shareable calculators with volumetric scenarios |
| RFP responses | Complete bidder response documents with technical depth |
| Requirements traceability matrices | Full RTM linking requirements to solution components |
| Technical BOM | Bill of Materials with service selections and sizing |
| Meeting prep briefs | Customer context, talking points, and engagement strategy |
| Email drafts | Post-meeting follow-ups, partner communications, outreach |
| Business value assessments (BVA) | Customer-ready BVAs with quantified impact |
| GTM 1-pagers | Industry-specific positioning |
| SOW (Statement of Work) | Scoped delivery documents with architecture and business value |
| APT / Program stories | Agentic Process Transformation narratives |
| Gap analysis documents | Well-Architected and architecture gap assessments |
| Strategy documents | Executive-level presentations and positioning |
| APN competency submissions | Partner program preparation materials |
| Implementation roadmaps | Phased delivery plans with milestones |
| Starter code | Application code, agent frameworks, and IaC templates |
| Compliance documentation | HIPAA, PCI-DSS, GDPR, and regional regulatory validation |`;

/** Getting Started section (collapsible) — rendered as markdown. */
export const HELP_GETTING_STARTED = `
1. **Submit your requirements** — Describe your project in the chat panel as a prompt. TACO will immediately begin working on your requirements.
2. **Attach files for context** — Upload files (PDF, images, text, markdown) along with your prompt to provide additional context such as baseline architectures, customer details, or problem statements.
3. **Provide Salesforce context** — You can provide Salesforce account and opportunity IDs, and TACO will pull the customer context from those Salesforce objects to enrich the solution.
4. **Watch the thinking** — While TACO is processing, you can view its thought process in real-time in the **Thinking** tab.
5. **View generated artifacts** — Once TACO finishes, your artifacts (summary, architecture diagram, implementation details, and additional artifacts) will appear in their respective tabs.
6. **Answer clarifying questions** — TACO may ask clarifying questions to extract and properly structure your requirements and identify gaps. Respond in the chat panel.
7. **Iterate on your solution** — When requirements change, TACO revamps the solution automatically by surgically editing the relevant artifacts.
8. **Download your artifacts** — All generated artifacts are downloadable for sharing and offline use.
9. **Multilingual output** — TACO supports multilingual output generation for global teams and customers.`;

// Keep the legacy HELP_CONTENT export for backward compatibility if needed.
export const HELP_CONTENT = `
# Welcome to TACO

${HELP_INTRO}

## Artifacts
${HELP_ARTIFACTS}

## Getting Started
${HELP_GETTING_STARTED}
`;
